import React from 'react';
import DOMPurify from 'dompurify';
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { cn } from "@/infrastructure/lib/utils";

export interface JsonViewerProps extends React.HTMLAttributes<HTMLPreElement> {
   // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  className?: string;
}

const syntaxHighlight = (json: string): string => {
  return json.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g, (match) => {
    let cls = 'text-emerald-600 dark:text-emerald-400'; // number
    if (/^"/.test(match)) {
      if (/:$/.test(match)) {
        cls = 'text-foreground font-medium'; // key
      } else {
        cls = 'text-blue-600 dark:text-blue-400'; // string
      }
    } else if (/true|false/.test(match)) {
      cls = 'text-purple-600 dark:text-purple-400'; // boolean
    } else if (/null/.test(match)) {
      cls = 'text-red-500 dark:text-red-400'; // null
    }
    return `<span class="${cls}">${match}</span>`;
  });
};

export const JsonViewer = ({ data, className, ...props }: JsonViewerProps) => {
  const formattedJson = React.useMemo(() => {
    try {
      const jsonString = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
      return syntaxHighlight(jsonString);
    } catch (error) {
      console.error('Error formatting JSON:', error);
      return String(data);
    }
  }, [data]);

  return (
    <ScrollArea className={cn(
      "relative rounded-lg border bg-surface-sunken/50",
      "shadow-[0_0_0_1px_rgba(0,0,0,0.05)]",
      "dark:shadow-[0_0_0_1px_rgba(255,255,255,0.05)]",
      className
    )}>
      <div className="overflow-x-auto">
        <pre
          {...props}
          className={cn(
            "font-mono text-[13px] leading-relaxed tracking-tight",
            "font-['SF_Mono',_'Monaco',_'Consolas',_monospace]",
            "p-4 whitespace-pre-wrap break-words"
          )}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formattedJson) }}
        />
      </div>
    </ScrollArea>
  );
};
